import Toast from "@/lib/components/Toast";
import { TimelineItemToAnimate } from "@/lib/stores/editor-store/editor-state";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { round } from "@/lib/utils";
import { CreativeProjectTimelineItem } from "@repo/api/creative-project";
import { AnimatedImageDto } from "@repo/api/animated-image";
import {
  getAnimatedImageConfig,
  setCompositionAnimatedImage,
} from "@repo/api/utils/composition";
import { getMaxDurationMs } from "@repo/video-engine-i2v/utils/timing";
import { useCallback, useEffect, useMemo, useState } from "react";

export type ImageListItemStatus =
  | "UNKNOWN"
  | "SELECTED"
  | "PROCESSING"
  | "FAILED"
  | "COMPLETED";

function getStatus(
  formData: TimelineItemToAnimate | undefined,
  animatedImage: AnimatedImageDto | undefined,
): ImageListItemStatus {
  if (formData) {
    return "SELECTED";
  }

  if (!animatedImage) {
    return "UNKNOWN";
  }

  if (
    animatedImage.status === "INITIAL" ||
    animatedImage.status === "POST_PROCESSING"
  ) {
    return "PROCESSING";
  }

  return animatedImage.status;
}

export function useTimelineItem(timelineItem: CreativeProjectTimelineItem) {
  const timelineItemFormData = useEditorStore(
    (state) => state.animationForm.items[timelineItem.externalId],
  );

  const sourceImages = useEditorStore((state) => state.sourceImages);
  const animatedImages = useEditorStore((state) => state.animatedImages);
  const editorService = useEditorStore((state) => state.service);

  const animatedImage = useMemo(() => {
    if (!timelineItem.animatedImageExternalId) {
      return undefined;
    }

    if (!animatedImages[timelineItem.animatedImageExternalId]) {
      throw new Error(
        `Animated image not found for ${timelineItem.animatedImageExternalId}`,
      );
    }

    return animatedImages[timelineItem.animatedImageExternalId];
  }, [animatedImages, timelineItem.animatedImageExternalId]);

  const sourceImage = useMemo(() => {
    if (!timelineItem.startSourceImageExternalId) {
      return undefined;
    }

    if (!sourceImages[timelineItem.startSourceImageExternalId]) {
      throw new Error(
        `Source image not found for ${timelineItem.startSourceImageExternalId}`,
      );
    }

    return sourceImages[timelineItem.startSourceImageExternalId];
  }, [sourceImages, timelineItem.startSourceImageExternalId]);

  const status = getStatus(timelineItemFormData, animatedImage);

  useEffect(() => {
    if (status === "UNKNOWN") {
      if (timelineItem.animatedImageExternalIds.length === 0) {
        editorService.selectTimelineItemForAnimation(
          timelineItem.externalId,
          true,
        );
      } else {
        editorService.updateVideo((video) => {
          const timelineItemToEdit =
            video.compositionJson.mainSceneTimeline.find(
              (itemToEdit) => itemToEdit.externalId === timelineItem.externalId,
            )!;

          setCompositionAnimatedImage({
            timelineItem: timelineItemToEdit,
            animatedImageExternalId: timelineItem.animatedImageExternalIds[0]!,
          });
        });
      }
    }
  }, [timelineItem, status, editorService]);

  const removeCompositionImage = useCallback(
    (externalId: string) => {
      editorService.updateVideo((video) => {
        video.compositionJson.mainSceneTimeline =
          video.compositionJson.mainSceneTimeline.filter(
            (timelineItem) => timelineItem.externalId !== externalId,
          );
      });
    },
    [editorService],
  );

  const setAnimatedImage = useCallback(
    (timelineItemExternalId: string, animatedImageExternalId: string) => {
      editorService.updateVideo((video) => {
        const timelineItemToEdit = video.compositionJson.mainSceneTimeline.find(
          (timelineItem) => timelineItem.externalId === timelineItemExternalId,
        )!;

        setCompositionAnimatedImage({
          timelineItem: timelineItemToEdit,
          animatedImageExternalId,
        });
      });
    },
    [editorService],
  );

  return {
    id: timelineItem.externalId,
    sourceImage,
    animatedImage,
    timelineItemFormData,
    status,
    selectTimelineItemForAnimation:
      editorService.selectTimelineItemForAnimation,
    removeCompositionImage,
    setAnimatedImage,
  };
}

export function useDuration(
  timelineItem: CreativeProjectTimelineItem,
  animatedImage: AnimatedImageDto,
) {
  const updateVideo = useEditorStore((state) => state.service.updateVideo);

  const value = getAnimatedImageConfig(timelineItem).duration ?? 2.5;

  const [tempValue, setTempValue] = useState<number | null>(null);

  const maxValue = useMemo(() => {
    try {
      const config = getAnimatedImageConfig(timelineItem);
      const maxDurationMs = getMaxDurationMs(animatedImage, config);
      return round(maxDurationMs / 1000, 3);
    } catch (error) {
      console.error(error);
      return 5;
    }
  }, [timelineItem, animatedImage]);

  const handleCommit = useCallback(
    (newValue: number, commitMode: "COMMIT" | "COMMIT_MULTIPLE" = "COMMIT") => {
      updateVideo((video) => {
        let timelineItemsToEdit = video.compositionJson.mainSceneTimeline;

        if (commitMode === "COMMIT") {
          timelineItemsToEdit = timelineItemsToEdit.filter(
            (item) => item.externalId === timelineItem.externalId,
          );
        }

        for (const item of timelineItemsToEdit) {
          const configToEdit = getAnimatedImageConfig(item);

          // newValue might be larger than maxValue for some clips but it doesn't matter.
          // Video engine timing logic will handle it and
          // useDuration hooks in other clips will automatically update to right value to UI.
          configToEdit.duration = newValue;
        }
      });

      setTempValue(null);
    },
    [setTempValue, updateVideo, timelineItem],
  );

  useEffect(() => {
    if (value > maxValue) {
      handleCommit(maxValue);
    }
  }, [value, maxValue, handleCommit]);

  return {
    value: tempValue ?? value,
    minValue: 0.1,
    maxValue,
    handleChange: setTempValue,
    handleCommit,
  };
}

export function useVolume(timelineItem: CreativeProjectTimelineItem) {
  const updateVideo = useEditorStore((state) => state.service.updateVideo);

  const value = getAnimatedImageConfig(timelineItem).volume ?? 0;
  const [tempValue, setTempValue] = useState<number | null>(null);

  const handleCommit = useCallback(
    (newValue: number) => {
      updateVideo((video) => {
        const timelineItemToEdit = video.compositionJson.mainSceneTimeline.find(
          (item) => item.externalId === timelineItem.externalId,
        )!;

        const configToEdit = getAnimatedImageConfig(timelineItemToEdit);
        configToEdit.volume = newValue;
      });

      setTempValue(null);
    },
    [updateVideo, timelineItem],
  );

  return {
    value: tempValue ?? value,
    minValue: 0,
    maxValue: 1,
    handleChange: setTempValue,
    handleCommit,
  };
}

export function usePlaybackSpeed(
  timelineItem: CreativeProjectTimelineItem,
  animatedImage: AnimatedImageDto,
) {
  const [isInitializingSlowMotion, setIsInitializingSlowMotion] =
    useState(false);
  const updateVideo = useEditorStore((state) => state.service.updateVideo);
  const editorService = useEditorStore((state) => state.service);

  const value = getAnimatedImageConfig(timelineItem).playbackSpeed ?? 1;
  const [tempValue, setTempValue] = useState<number | null>(null);

  const handleCommit = useCallback(
    async (newValue: number) => {
      updateVideo((video) => {
        const timelineItemToEdit = video.compositionJson.mainSceneTimeline.find(
          (item) => item.externalId === timelineItem.externalId,
        )!;

        const configToEdit = getAnimatedImageConfig(timelineItemToEdit);

        configToEdit.playbackSpeed = newValue;
      });

      // Uploaded videos are played back as-is, without a generated slow motion source
      if (
        newValue < 1 &&
        animatedImage.type !== "UPLOADED" &&
        timelineItem.animatedImageExternalId &&
        !isInitializingSlowMotion
      ) {
        setIsInitializingSlowMotion(true);

        try {
          await editorService.initializeSlowMotion({
            animatedImageExternalId: timelineItem.animatedImageExternalId,
            provider: "amplifiles/slow-motion-1.0",
          });
        } catch (error) {
          console.error(error);
          Toast.error("Failed to create a smoothed clip.");
        } finally {
          setIsInitializingSlowMotion(false);
        }
      }

      setTempValue(null);
    },
    [
      setTempValue,
      updateVideo,
      timelineItem,
      animatedImage,
      setIsInitializingSlowMotion,
      isInitializingSlowMotion,
      editorService,
    ],
  );

  return {
    value: tempValue ?? value,
    minValue: 0.25,
    maxValue: 2,
    handleChange: setTempValue,
    handleCommit,
  };
}
